// Bullet tracers: thin camera-facing ribbons (one instanced draw call). A tracer is a short bright
// streak (2.4 m for the player's own shots, 4.5 m for others) that races from the muzzle to the
// impact point in ~35 ms and is gone by ~55 ms, so even a fast gun shows separate streaks.

import * as THREE from "three";

const VERT = /* glsl */ `
attribute vec3 iA;
attribute vec3 iB;
attribute vec4 iP;
attribute vec3 iColor;
varying float vAlong;
varying float vSide;
varying float vAlpha;
varying vec3 vColor;
void main() {
  vec3 pw = mix(iA, iB, position.y);
  vec4 pv = viewMatrix * vec4(pw, 1.0);
  vec3 axisV = (viewMatrix * vec4(iB - iA, 0.0)).xyz;
  vec3 side = cross(axisV, normalize(pv.xyz));
  float l = length(side);
  side = l > 1e-4 ? side / l : vec3(1.0, 0.0, 0.0);
  float taper = mix(0.12, 1.0, position.y);
  pv.xyz += side * position.x * iP.x * taper;
  gl_Position = projectionMatrix * pv;
  vAlong = position.y;
  vSide = position.x * 2.0;
  vAlpha = iP.y;
  vColor = iColor;
}
`;

const FRAG = /* glsl */ `
varying float vAlong;
varying float vSide;
varying float vAlpha;
varying vec3 vColor;
void main() {
  float core = 1.0 - smoothstep(0.0, 1.0, abs(vSide));
  float a = vAlpha * core * (0.25 + 0.75 * vAlong);
  gl_FragColor = vec4(vColor * (1.0 + 1.5 * core * core), a);
  if (a < 0.004) discard;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

const LIFE = 0.055;
/** Fraction of the life the head spends travelling from the muzzle to the target. */
const TRAVEL = 0.65;
/** Floats per tracer: from(3), to(3), age, width, brightness, r, g, b, longest streak (m). */
const STRIDE = 13;
const STREAK_LOCAL = 2.4;
const STREAK_REMOTE = 4.5;

export class TracerBatch {
  readonly mesh: THREE.Mesh;
  private readonly geometry = new THREE.InstancedBufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private readonly iA: THREE.InstancedBufferAttribute;
  private readonly iB: THREE.InstancedBufferAttribute;
  private readonly iP: THREE.InstancedBufferAttribute;
  private readonly iColor: THREE.InstancedBufferAttribute;
  // Per tracer: from(3), to(3), age, width, brightness, r, g, b
  private readonly data: Float32Array;
  private count = 0;
  private readonly capacity: number;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.data = new Float32Array(capacity * STRIDE);
    // Quad: x across (-0.5..0.5), y along (0 = tail, 1 = head).
    this.geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array([-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0]), 3),
    );
    this.geometry.setIndex([0, 1, 2, 0, 2, 3]);
    this.iA = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.iB = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.iP = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    this.iColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    for (const a of [this.iA, this.iB, this.iP, this.iColor]) a.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute("iA", this.iA);
    this.geometry.setAttribute("iB", this.iB);
    this.geometry.setAttribute("iP", this.iP);
    this.geometry.setAttribute("iColor", this.iColor);
    this.geometry.instanceCount = 0;
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      fog: false,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = "tracers";
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 21;
    this.mesh.visible = false;
  }

  /** `local` tracers (the player's own shots) are thinner and a touch dimmer. */
  add(fx: number, fy: number, fz: number, tx: number, ty: number, tz: number, local: boolean): void {
    let slot = this.count;
    if (slot >= this.capacity) slot = 0; // overwrite the oldest-ish
    else this.count++;
    const o = slot * STRIDE;
    const d = this.data;
    d[o] = fx;
    d[o + 1] = fy;
    d[o + 2] = fz;
    d[o + 3] = tx;
    d[o + 4] = ty;
    d[o + 5] = tz;
    d[o + 6] = 0;
    d[o + 7] = local ? 0.009 : 0.03;
    d[o + 8] = local ? 0.8 : 1;
    d[o + 9] = 1.0;
    d[o + 10] = 0.9;
    d[o + 11] = 0.62;
    d[o + 12] = local ? STREAK_LOCAL : STREAK_REMOTE;
  }

  clear(): void {
    this.count = 0;
    this.geometry.instanceCount = 0;
    this.mesh.visible = false;
  }

  update(dt: number): void {
    const d = this.data;
    const a = this.iA.array as Float32Array;
    const b = this.iB.array as Float32Array;
    const p = this.iP.array as Float32Array;
    const c = this.iColor.array as Float32Array;
    let n = this.count;
    let i = 0;
    while (i < n) {
      const o = i * STRIDE;
      const age = d[o + 6] + dt;
      if (age >= LIFE) {
        n--;
        if (i !== n) d.copyWithin(o, n * STRIDE, n * STRIDE + STRIDE);
        continue;
      }
      d[o + 6] = age;
      const u = age / LIFE;
      const fx = d[o];
      const fy = d[o + 1];
      const fz = d[o + 2];
      const dx = d[o + 3] - fx;
      const dy = d[o + 4] - fy;
      const dz = d[o + 5] - fz;
      const dist = Math.hypot(dx, dy, dz);
      const inv = dist > 1e-6 ? 1 / dist : 0;
      const headT = Math.min(u / TRAVEL, 1);
      const headD = dist * headT;
      const streak = Math.min(d[o + 12] as number, dist) * (1 - 0.5 * u);
      const tailD = Math.max(0, headD - streak);
      const k3 = i * 3;
      a[k3] = fx + dx * inv * tailD;
      a[k3 + 1] = fy + dy * inv * tailD;
      a[k3 + 2] = fz + dz * inv * tailD;
      b[k3] = fx + dx * inv * headD;
      b[k3 + 1] = fy + dy * inv * headD;
      b[k3 + 2] = fz + dz * inv * headD;
      const k4 = i * 4;
      const fade = 1 - u;
      p[k4] = d[o + 7];
      p[k4 + 1] = d[o + 8] * fade * fade;
      p[k4 + 2] = 0;
      p[k4 + 3] = 0;
      const bright = 2.2;
      c[k3] = d[o + 9] * bright;
      c[k3 + 1] = d[o + 10] * bright;
      c[k3 + 2] = d[o + 11] * bright;
      i++;
    }
    this.count = n;
    this.geometry.instanceCount = n;
    this.mesh.visible = n > 0;
    if (n > 0) {
      this.upload(this.iA, n * 3);
      this.upload(this.iB, n * 3);
      this.upload(this.iColor, n * 3);
      this.upload(this.iP, n * 4);
    }
  }

  private upload(attr: THREE.InstancedBufferAttribute, count: number): void {
    attr.clearUpdateRanges();
    attr.addUpdateRange(0, count);
    attr.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}
